import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  SentimentVeryDissatisfied as BadIcon,
  SentimentDissatisfied as PoorIcon,
  SentimentNeutral as NeutralIcon,
  SentimentSatisfied as GoodIcon,
  SentimentVerySatisfied as ExcellentIcon,
} from '@mui/icons-material';

interface ProbabilityIndicatorProps {
  value: number | null;
  compact?: boolean;
}

const ProbabilityIndicator: React.FC<ProbabilityIndicatorProps> = ({ value, compact = false }) => {
  const theme = useTheme();

  if (value === null) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: compact ? 'center' : 'flex-start', 
          opacity: 0.6 
        }}
      >
        <NeutralIcon sx={{ color: theme.palette.grey[500], fontSize: compact ? 16 : 20 }} />
        <Typography 
          variant={compact ? "caption" : "body2"} 
          color="textSecondary" 
          sx={{ ml: 0.5, fontWeight: 500 }}
        >
          {compact ? "?" : "Inte satt"}
        </Typography>
      </Box>
    );
  }

  // Determine which icon and color to use based on probability value
  const getIconAndColor = (probability: number) => {
    if (probability < 20) {
      return { 
        icon: BadIcon, 
        color: theme.palette.error.main,
        label: 'Mycket låg'
      };
    } else if (probability < 40) {
      return { 
        icon: PoorIcon, 
        color: theme.palette.error.light,
        label: 'Låg'
      };
    } else if (probability < 60) {
      return { 
        icon: NeutralIcon, 
        color: theme.palette.warning.main,
        label: 'Medel'
      };
    } else if (probability < 80) {
      return { 
        icon: GoodIcon, 
        color: theme.palette.success.light,
        label: 'Hög'
      };
    } else {
      return { 
        icon: ExcellentIcon, 
        color: theme.palette.success.main,
        label: 'Mycket hög'
      };
    }
  };

  const { icon: Icon, color, label } = getIconAndColor(value);

  // Function to render the circular progress
  const renderCircularProgress = () => {
    const size = compact ? 32 : 40;
    const thickness = compact ? 2 : 3;
    const fontSize = compact ? '0.65rem' : '0.75rem';

    return (
      <Box
        sx={{
          position: 'relative',
          display: 'inline-flex',
          mr: compact ? 0.5 : 1,
        }}
      >
        {/* Background circle */}
        <Box
          sx={{
            width: size,
            height: size,
            borderRadius: '50%',
            bgcolor: theme.palette.grey[200],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {/* Colored progress circle using conic gradient */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              background: `conic-gradient(${color} ${value}%, transparent ${value}%)`,
              clipPath: 'circle(50%)',
            }}
          />
          
          {/* Center white circle */}
          <Box
            sx={{
              position: 'absolute',
              top: thickness,
              left: thickness,
              width: `calc(100% - ${thickness * 2}px)`,
              height: `calc(100% - ${thickness * 2}px)`,
              borderRadius: '50%',
              bgcolor: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography 
              sx={{ 
                fontSize, 
                fontWeight: 600, 
                color: theme.palette.text.primary 
              }}
            >
              {value}%
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box 
      sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: compact ? 'center' : 'flex-start',
      }}
    >
      {renderCircularProgress()}
      
      {!compact && (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, color }}>
            {label}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Icon sx={{ fontSize: 16, color, mr: 0.5 }} />
            <Typography variant="caption" color="textSecondary">
              Sannolikhet
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProbabilityIndicator;